import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../services/api.js';

const Context = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const [formData, setFormData] = useState({
    purpose: '',
    user_description: '',
    connected_systems: '',
    accessible_data: '',
    forbidden_data: '',
    allowed_actions: '',
    forbidden_actions: '',
  });

  // Refs for each textarea
  const textAreasRef = useRef({});
  const [toasts, setToasts] = useState([]);

  const addSuccessToast = (message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type: "success" }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  };

  const addErrorToast = (message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type: "error" }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  };

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  useEffect(() => {
    const savedData = sessionStorage.getItem("applicationData");
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);
  const [loading, setLoading] = useState(false);


  // Auto resize textarea
  const autoResize = (name) => {
    const ta = textAreasRef.current[name];
    if (ta) {
      ta.style.height = 'auto'; // reset height
      ta.style.height = ta.scrollHeight + 'px'; // set to content height
    }
  };
  const [isUpdate, setIsUpdate] = useState(false);
  
  useEffect(() => {
  const savedData = sessionStorage.getItem("applicationData");
  if (savedData) {
    const parsed = JSON.parse(savedData);
    setFormData(parsed);
    
    // Check if any field has content
    const hasContent = Object.values(parsed).some(val => (val ?? "").toString().trim() !== "");
    setIsUpdate(hasContent);
  }
}, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...formData, [name]: value };
    setFormData(updatedForm);
    sessionStorage.setItem("applicationData", JSON.stringify(updatedForm));
    autoResize(name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const endpoint = `${process.env.REACT_APP_DJANGO_API_URL}/update-application-context`

    try {
      const applicationData = sessionStorage.getItem("applicationData")
        ? JSON.parse(sessionStorage.getItem("applicationData"))
        : formData;

      const selectedApp = sessionStorage.getItem("selectedApp");

      // Construct payload to send
      const payload = {
        application_data: applicationData,
        selected_app: selectedApp, // 👈 include as extra field
      };

      const response = await API.post(endpoint, payload);
      if (response.status === 200) {
        addSuccessToast(response.data.message || "Application context updated.");
        sessionStorage.setItem("applicationData", JSON.stringify(formData));
        setTimeout(() => navigate("/newscan/endpoints"), 1000);
      } else {
        const msg = response.data?.error || "Failed to update application context.";
        addErrorToast(msg);
      }
    } catch (err) {
      console.error("Submission error:", err);
    }
    finally{
      setLoading(false);
    }
  };

  // Auto resize all textareas on load
  useEffect(() => {
    Object.keys(formData).forEach(name => autoResize(name));
  }, [formData]);

  return (
    <div className="p-8 min-h-screen m-8 mt-4">
      {/* Toast Notifications */}
      <div className="fixed top-5 right-5 z-[1000] flex flex-col space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              px-5 py-3 mb-2 rounded-lg flex items-center gap-3 shadow-lg animate-slide-in
              border
              ${toast.type === "error"
                ? "bg-red-400/80 border-red-200 text-red-900"
                :"bg-green-500/20 border-green-400/80 text-green-400"}
              backdrop-blur-xl
              transition
            `}
            style={{ minWidth: "250px", backdropFilter: "blur(12px)" }}
          >
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className={`hover:text-black text-lg font-bold focus:outline-none ${
                toast.type === "error" ? "text-red-900" : "text-green-900"
              }`}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {[{ 
            label: "What is the core business purpose of the AI within this application?", 
            name: "purpose", 
            rows: 4, 
            placeholder: "Example: The AI serves as a virtual travel advisor, enabling users to search for and book cost-effective flights to European destinations." 
          },
          { 
            label: "Which user persona is being represented during this red team evaluation?", 
            name: "user_description", 
            rows: 2, 
            placeholder: "Example: A traveller seeking affordable flight options within Europe." 
          },
          { 
            label: "What external or internal systems are connected to the AI application?", 
            name: "connected_systems", 
            rows: 2, 
            placeholder: "Example: An airline reservation platform that provides real-time flight availability, pricing, and scheduling information." 
          },
          { 
            label: "What data is the AI authorized to retrieve and display to the user based on their role?", 
            name: "accessible_data", 
            rows: 2, 
            placeholder: "Example: The user’s own travel history, saved preferences, and current flight availability." 
          },
          { 
            label: "What data must remain inaccessible to the user to ensure confidentiality and data segregation?", 
            name: "forbidden_data", 
            rows: 2, 
            placeholder: "Example: Other users’ personal data or booking records, internal business insights, and non-public financial information." 
          },
          { 
            label: "What actions is the user allowed to perform through the AI interface?", 
            name: "allowed_actions", 
            rows: 2, 
            placeholder: "Example: Search for flights, make personal bookings, and view their historical reservations." 
          },
          { 
            label: "What actions must be restricted to enforce principle-of-least-privilege and prevent unauthorized activity?", 
            name: "forbidden_actions", 
            rows: 2, 
            placeholder: "Example: Accessing or modifying bookings made by other users, or interacting with administrative-level features." 
          }].map(({ label, name, rows, placeholder }) => (
          <div key={name} >
            <p className="text-white text-xl  mt-4">{label}</p>
            <textarea
              ref={el => textAreasRef.current[name] = el}
              name={name}
              value={formData[name]}
              onChange={handleChange}
              readOnly={role === 'viewer'}
              rows={rows}
              className={`w-full text-white bg-[#212121] border border-[#232325] m-5 p-3 rounded-xl resize-none overflow-hidden ${
                role === 'viewer' ? 'opacity-75 cursor-not-allowed bg-[#1a1a1a]' : ''
              }`}
              placeholder={placeholder}
            />
          </div>
        ))}

        {role !== 'viewer' && (
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center gap-2 bg-blue-600/20 text-blue-400 border border-blue-400 px-6 py-2 rounded-xl cursor-pointer hover:bg-blue-600/30 transition-all duration-200 ${
                loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              {loading && (
                <svg
                  className="animate-spin h-5 w-5 text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              )}
              {loading ? (isUpdate ? "Updating..." : "Submitting...") : (isUpdate ? "Update" : "Submit")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default Context;
